import axios, { AxiosResponse } from "axios";
import { ResponseObj } from "@shared/interface";
import { getUrl } from "@shared/constants/api";
import { store } from "@store/store";
import { clearCredentials } from "@store/slices/authSlice";

/* -------------------------------------------------------------------------- */
/*                                Axios Setup                                 */
/* -------------------------------------------------------------------------- */

const axiosInstance = axios.create();
const CancelToken = axios.CancelToken;
let cancel_req: any;

export { cancel_req };

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

export interface AxiosParams {
  method: string;
  url: string;
  data?: any;
  isAccessTokenRequire?: boolean;
  contentType?: string;
  customMessages?: {
    success?: string;
    error?: string;
  };
  useBackendMessage?: boolean;
}

export interface MiscellaneousRequestParams {
  isAccessTokenRequire?: boolean;
  contentType?: string;
  customMessages?: {
    success?: string;
    error?: string;
  };
  useBackendMessage?: boolean;
}

/* --------------------------------------------------------------------------- */
/*                            Request Interceptor                              */
/* --------------------------------------------------------------------------- */

axiosInstance.interceptors.request.use(
  (config: any) => {
    const state = store.getState();
    const token = state.auth.token;

    if (token && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* ---------------------------------------------------------------------------- */
/*                           Response Interceptor                               */
/* ---------------------------------------------------------------------------- */

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      store.dispatch(clearCredentials());
    }

    return Promise.reject(error);
  }
);

/* -------------------------------------------------------------------------- */
/*                               HTTP Methods                                 */
/* -------------------------------------------------------------------------- */

const get = (url: string, params: any = {}, otherData: MiscellaneousRequestParams = {}) => {
  return commonAxios({
    method: "GET",
    url: getUrl(url, params),
    ...otherData,
  });
};

const post = (
  url: string,
  params: any = {},
  queryParams = {},
  otherData: MiscellaneousRequestParams = {}
) => {
  return commonAxios({
    method: "POST",
    url: getUrl(url, queryParams),
    data: params,
    ...otherData,
  });
};

const put = (url: string, params: any = {}, otherData: MiscellaneousRequestParams = {}) => {
  return commonAxios({
    method: "PUT",
    url: getUrl(url),
    data: params,
    ...otherData,
  });
};

const deleteRequest = (
  url: string,
  params: any = {},
  otherData: MiscellaneousRequestParams = {}
) => {
  return commonAxios({
    method: "DELETE",
    url: getUrl(url),
    data: params,
    ...otherData,
  });
};

const patch = (url: string, params: any = {}, otherData: MiscellaneousRequestParams = {}) => {
  return commonAxios({
    method: "PATCH",
    url: getUrl(url),
    data: params,
    ...otherData,
  });
};

/* -------------------------------------------------------------------------- */
/*                               Core Axios                                   */
/* -------------------------------------------------------------------------- */

const commonAxios = ({
  method,
  url,
  data,
  isAccessTokenRequire = true,
  contentType = "application/json",
  customMessages,
  useBackendMessage = true,
}: AxiosParams): Promise<any> => {
  const headers: any = {};

  const isFormData = data instanceof FormData;

  if (!isFormData) {
    headers["Content-Type"] = contentType;
  }

  const state = store.getState();
  const token = isAccessTokenRequire ? state.auth.token : null;

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else {
    headers["x-request-language"] = localStorage.getItem("lang") || "de";
  }

  return new Promise((resolve, reject) => {
    axiosInstance({
      method,
      url,
      headers,
      data: isFormData ? data : contentType === "application/json" ? data : data,
      cancelToken: new CancelToken((c) => {
        cancel_req = c;
      }),
    })
      .then((response: AxiosResponse<ResponseObj<any>>) => {
        const successMessage = useBackendMessage
          ? response.data.message || customMessages?.success
          : customMessages?.success || response.data.message;

        const responseData = response.data.data;

        if (responseData?.data && responseData?.pagination) {
          resolve({
            data: responseData.data,
            pagination: responseData.pagination,
            message: successMessage,
          });
        } else {
          resolve({
            data: responseData,
            message: successMessage,
          });
        }
      })
      .catch((error) => {
        const errorMessage = useBackendMessage
          ? error.response?.data?.message || customMessages?.error || error.message
          : customMessages?.error || error.response?.data?.message || error.message;

        reject({
          error,
          message: errorMessage || "Something went wrong",
        });
      });
  });
};

/* -------------------------------------------------------------------------- */
/*                                  Export                                    */
/* -------------------------------------------------------------------------- */

const httpService = {
  get,
  post,
  put,
  deleteRequest,
  patch,
};

export { axiosInstance };
export default httpService;
